import React, { useRef, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './PortfolioSection.css';

// Image protection handlers
const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
};

const handleDragStart = (e: any) => {
    e.preventDefault();
    return false;
};

const portfolioItems = [
    {
        id: 1,
        title: "Project Alpha",
        category: "Wedding",
        image: "https://res.cloudinary.com/dhqmcmekn/image/upload/v1764785933/wed7_kwmbja_u26jtx.webp",
        fullImage: "https://res.cloudinary.com/dhqmcmekn/image/upload/v1764785933/wed7_kwmbja_u26jtx.webp"
    },
    {
        id: 2,
        title: "Project Beta",
        category: "Portrait",
        image: "https://res.cloudinary.com/dhqmcmekn/image/upload/v1764785931/DSC05804-Edited-2_copy_whthcv_tttvwk.webp",
        fullImage: "https://res.cloudinary.com/dhqmcmekn/image/upload/v1764785931/DSC05804-Edited-2_copy_whthcv_tttvwk.webp"
    },
    {
        id: 3,
        title: "Project Gamma",
        category: "Event",
        image: "https://res.cloudinary.com/dhqmcmekn/image/upload/v1764785929/28_ok8pus_sgkqyc.webp",
        fullImage: "https://res.cloudinary.com/dhqmcmekn/image/upload/v1764785929/28_ok8pus_sgkqyc.webp"
    },
    {
        id: 4,
        title: "Project Delta",
        category: "Commercial",
        image: "https://res.cloudinary.com/dhqmcmekn/image/upload/v1764785935/wed8_ndmjtl_kibwcs.webp",
        fullImage: "https://res.cloudinary.com/dhqmcmekn/image/upload/v1764785935/wed8_ndmjtl_kibwcs.webp"
    },
    {
        id: 5,
        title: "Project Epsilon",
        category: "Fashion",
        image: "https://res.cloudinary.com/dhqmcmekn/image/upload/v1764754961/DSC_0685_xnbzkv.webp",
        fullImage: "https://res.cloudinary.com/dhqmcmekn/image/upload/v1764754961/DSC_0685_xnbzkv.webp"
    },
    {
        id: 6,
        title: "Project Zeta",
        category: "Documentary",
        image: "https://res.cloudinary.com/dhqmcmekn/image/upload/v1764754952/DSC_0516_copy_j8pypc.webp",
        fullImage: "https://res.cloudinary.com/dhqmcmekn/image/upload/v1764754952/DSC_0516_copy_j8pypc.webp"
    }
];

const PortfolioSection: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const [selectedImage, setSelectedImage] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"]
    });

    const headerY = useTransform(scrollYProgress, [0.1, 0.3], [60, 0]);
    const headerOpacity = useTransform(scrollYProgress, [0.1, 0.3], [0, 1]);

    const openModal = (index: number) => {
        setSelectedImage(index);
        setIsLoading(true);
        document.body.style.overflow = 'hidden';
    };

    const closeModal = () => {
        setSelectedImage(null);
        setIsLoading(false);
        document.body.style.overflow = 'unset';
    };

    const nextImage = () => {
        if (selectedImage !== null) {
            setIsLoading(true);
            setSelectedImage((selectedImage + 1) % portfolioItems.length);
        }
    };

    const prevImage = () => {
        if (selectedImage !== null) {
            setIsLoading(true);
            setSelectedImage((selectedImage - 1 + portfolioItems.length) % portfolioItems.length);
        }
    };

    const handleImageLoad = () => {
        setIsLoading(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') closeModal();
        if (e.key === 'ArrowRight') nextImage();
        if (e.key === 'ArrowLeft') prevImage();
    };

    const minSwipeDistance = 50;

    const onTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            nextImage();
        } else if (isRightSwipe) {
            prevImage();
        }
    };

    return (
        <div className="portfolio-section" id="portfolio" ref={containerRef}>
            <div className="portfolio-container">
                <motion.div
                    className="portfolio-header"
                    style={{ y: headerY, opacity: headerOpacity }}
                >
                    <h2 className="section-title dark">
                        OUR <span className="title-accent">WORK</span>
                    </h2>
                    <h2 className="section-title dark indent">
                        THROUGH THE <span className="title-accent">LENS</span>
                    </h2>
                </motion.div>

                <div className="portfolio-grid">
                    {portfolioItems.map((item, index) => (
                        <motion.div
                            key={item.id}
                            className="portfolio-item"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            viewport={{ once: false }}
                            onClick={() => openModal(index)}
                        >
                            <div className="portfolio-image-container">
                                <img 
                                    src={item.image} 
                                    alt={item.title}
                                    className="portfolio-image"
                                    loading="lazy"
                                    onContextMenu={handleContextMenu}
                                    onDragStart={handleDragStart}
                                    style={{ pointerEvents: 'none' }}
                                />
                                <div className="portfolio-overlay">
                                    <h3 className="portfolio-title">{item.title}</h3>
                                    <p className="portfolio-category">{item.category}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <motion.div 
                    className="portfolio-cta"
                    style={{ 
                        opacity: useTransform(scrollYProgress, [0.4, 0.6], [0, 1]),
                        y: useTransform(scrollYProgress, [0.4, 0.6], [50, 0])
                    }}
                >
                    <button
                        className="full-gallery-button"
                        type="button"
                        onClick={() => navigate('/gallery')}
                    >
                        FULL GALLERY <span className="gallery-arrow">→</span>
                    </button>
                </motion.div>
            </div>

            {/* Image Modal */}
            <AnimatePresence>
                {selectedImage !== null && (
                    <motion.div
                        className="image-modal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeModal}
                        onKeyDown={handleKeyDown}
                        tabIndex={0}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        <motion.div
                            className="modal-content"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button className="modal-close" onClick={closeModal}>
                                ×
                            </button>
                            
                            <button className="modal-nav prev" onClick={prevImage}>
                                ←
                            </button>
                            
                            <div className="modal-image-container">
                            {isLoading && (
                                <div className="image-loader">
                                    <div className="spinner"></div>
                                </div>
                            )}
                            <img 
                                src={portfolioItems[selectedImage].fullImage}
                                alt={portfolioItems[selectedImage].title}
                                className="modal-image"
                                loading="lazy"
                                onLoad={handleImageLoad}
                                onContextMenu={handleContextMenu}
                                onDragStart={handleDragStart}
                                style={{ opacity: isLoading ? 0 : 1, pointerEvents: 'none' }}
                            />
                        </div>
                            
                            <button className="modal-nav next" onClick={nextImage}>
                                →
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default PortfolioSection;
